/**
 * rosaView.js — Render della schermata Rosa
 * Task 4: CRUD completo + lista giocatori; Task 5: moduli con copertura.
 */

import { getRoleChipClass } from '../features/filters/index.js';
import { escapeHtml } from '../utils/escapeHtml.js';
import { playerToKey } from '../utils/playerKey.js';
import { resolveStudioPlayerMantraRoles } from './studioCalc.js';
import { MANTRA_ROLES } from './studioCalc.js';
import {
  MANTRA_MODULE_IDS,
  MANTRA_MODULE_LINES,
  MANTRA_MODULES,
} from './mantraModules.js';
import {
  assignModuleSlots,
  computeModuleCoverage,
  computeRosaSummary,
  primaryMantraRole,
} from './rosaCalc.js';
import {
  buildModuleRoleTargets,
  buildRoleCoverageRows,
  countOwnedByRole,
} from './rosaModuleTargets.js';

/**
 * Renderizza la schermata Rosa.
 * @this {FantAPP}
 */
export const rosaViewMethods = {
  renderRosaView() {
    this._renderRosaStrip();
    this._renderRosaListByRole();
    this._renderRosaPitch?.();
    this._renderRosaModules();
    this._bindRosaModulesModal();
  },

  _bindRosaModulesModal() {
    const openBtn = document.getElementById('rosa-coverage-open-btn');
    const modal = document.getElementById('rosa-modules-modal');
    if (!openBtn || !modal) return;

    const close = () => {
      modal.hidden = true;
      modal.classList.remove('is-open');
      if (this._rosaModulesEscHandler) {
        document.removeEventListener('keydown', this._rosaModulesEscHandler);
        this._rosaModulesEscHandler = null;
      }
    };

    const open = () => {
      this._renderRosaModules();
      modal.hidden = false;
      modal.classList.add('is-open');
      if (this._rosaModulesEscHandler) {
        document.removeEventListener('keydown', this._rosaModulesEscHandler);
      }
      this._rosaModulesEscHandler = (event) => {
        if (event.key === 'Escape') close();
      };
      document.addEventListener('keydown', this._rosaModulesEscHandler);
    };

    openBtn.onclick = open;
    const closeBtn = modal.querySelector('.rosa-modules-modal-close');
    if (closeBtn) closeBtn.onclick = close;
    modal.onclick = (event) => {
      if (event.target === modal) close();
    };
  },

  _renderRosaStrip() {
    const el = document.getElementById('rosa-strip');
    if (!el) return;
    const playersByKey = new Map(
      (this.state.allPlayers || []).map((player) => [playerToKey(player), player]),
    );
    const entries = Array.from(this.state.roster || [], ([key, entry]) => ({ key, ...entry }));
    const budget = Number(this.state.rosaBudget) || 0;
    const maxPlayers = Number(this.state.rosaMaxPlayers) || 0;
    const summary = computeRosaSummary({
      entries,
      budget,
      roleTargets: this.state.rosaRoleTargets || {},
      maxPlayers,
      playersByKey,
    });
    const spentPercent = budget > 0
      ? Math.min(100, Math.round((summary.spent / budget) * 100))
      : 0;
    const auctionName = this.state.activeAuctionName || 'Asta';

    el.innerHTML = `
      <div class="rosa-budget-block">
        <span class="rosa-strip-label">Residui</span>
        <strong class="rosa-budget-residual num">${summary.residui}M</strong>
        <span class="rosa-budget-meta">SPESO <strong class="num">${summary.spent}M</strong> / TOT <strong class="num">${budget}M</strong></span>
        <div class="rosa-budget-bar" aria-label="Budget speso ${summary.spent} su ${budget}">
          <span class="rosa-budget-fill" style="width: ${spentPercent}%"></span>
        </div>
        <span class="rosa-strip-auction-name">${escapeHtml(auctionName)}</span>
      </div>
      <div class="rosa-strip-stats">
        <div class="rosa-strip-stat"><span>ROSA</span><strong class="num">${summary.owned}/${summary.slots}</strong></div>
        <div class="rosa-strip-stat"><span>MEDIA</span><strong class="num">${summary.media}M</strong></div>
        <div class="rosa-strip-stat"><span>MAX OFFERTA</span><strong class="num">${Math.max(0, summary.residui)}M</strong></div>
        <div class="rosa-strip-stat rosa-strip-stat--top"><span>TOP</span><strong>${escapeHtml(summary.topAcquistoName)}${summary.topAcquistoPrice ? ` <em class="num">${summary.topAcquistoPrice}M</em>` : ''}</strong></div>
      </div>
      <div class="rosa-strip-actions">
        <button type="button" class="rosa-settings-btn" id="rosa-settings-btn"
                data-testid="rosa-settings">Impostazioni</button>
        <button type="button" class="rosa-find-btn" id="rosa-find-svincolato-btn"
                data-testid="rosa-find-svincolato">Trova svincolato</button>
      </div>
    `;

    el.querySelector('#rosa-settings-btn')?.addEventListener('click', () => this.openAuctionSettings?.());
    el.querySelector('#rosa-find-svincolato-btn')?.addEventListener('click', () => this.switchAuctionScreen('lista'));
  },

  _resolveRosterPlayers() {
    const roster = this.state.roster;
    const allPlayers = this.state.allPlayers || [];
    if (!roster || roster.size === 0) return [];

    const byKey = new Map();
    for (const p of allPlayers) {
      const k = playerToKey(p);
      if (k) byKey.set(k, p);
    }

    const players = [];
    for (const key of roster.keys()) {
      const player = byKey.get(key);
      const roles = player ? resolveStudioPlayerMantraRoles(player) : [];
      players.push({
        playerKey: key,
        name: player?.name || key.split('|')[0] || key,
        roles,
      });
    }
    return players;
  },

  _renderRosaPitch() {
    const el = document.getElementById('rosa-pitch');
    if (!el) return;

    const savedModule = this.state.rosaSelectedModule;
    const moduleId = MANTRA_MODULES[savedModule] ? savedModule : '4-3-3';
    const assignments = assignModuleSlots(this._resolveRosterPlayers(), moduleId);
    const lines = MANTRA_MODULE_LINES[moduleId];
    let offset = 0;
    const groupedLines = lines.map((count) => {
      const slots = assignments.slice(offset, offset + count);
      offset += count;
      return slots;
    }).reverse();
    // Ruoli linea visuali (attacco in alto → portiere in basso), n linee variabili.
    const lineRoles = groupedLines.map((_, index) => {
      if (index === 0) return 'att';
      if (index === groupedLines.length - 1) return 'por';
      if (index === groupedLines.length - 2) return 'dif';
      return 'cen';
    });

    const renderSlot = (slot, lineRole) => {
      const preferred = slot.preferredRole;
      const highlightRole = slot.deployedRole || preferred;
      const roleOrder = preferred
        ? [...new Set([highlightRole, preferred, ...(slot.acceptedRoles || [])])]
        : (slot.acceptedRoles || []);
      const rolesHtml = roleOrder.length
        ? `<div class="rosa-pitch-slot-roles roles-container">${roleOrder
            .map((r) => {
              const prefCls = r === highlightRole ? ' role-chip--preferred' : '';
              return `<span class="${getRoleChipClass(r)}${prefCls}">${escapeHtml(r)}</span>`;
            })
            .join('')}</div>`
        : '';
      const isFilled = Boolean(slot.playerKey);
      const price = isFilled ? Number(this.state.roster?.get(slot.playerKey)?.price) || 0 : 0;
      const circleText = isFilled ? String(price) : '';
      return `
        <div class="rosa-pitch-slot rosa-pitch-slot--${lineRole}${isFilled ? '' : ' rosa-pitch-slot--empty'}"
             data-player-key="${isFilled ? escapeHtml(slot.playerKey) : ''}">
          <span class="rosa-pitch-slot-circle" aria-hidden="true">${escapeHtml(circleText)}</span>
          <span class="rosa-pitch-slot-name" title="${isFilled ? escapeHtml(slot.name) : ''}">${isFilled ? escapeHtml(slot.name) : 'Vuoto'}</span>
          ${rolesHtml}
        </div>
      `;
    };

    const maxPlayers = Number(this.state.rosaMaxPlayers) || 0;
    const targets = buildModuleRoleTargets(moduleId, maxPlayers);
    const owned = countOwnedByRole(this._resolveRosterPlayers());
    const rows = buildRoleCoverageRows(owned, targets);
    const coverageHtml = rows.length
      ? `<div class="rosa-module-role-coverage" data-testid="rosa-module-role-coverage">
          <div class="rosa-module-role-coverage-title">Copertura rosa · ${escapeHtml(moduleId)}</div>
          <div class="rosa-module-role-coverage-grid">
            ${rows.map((r) => `
              <div class="rosa-role-cov rosa-role-cov--${r.status}" data-testid="rosa-role-cov-${escapeHtml(r.role)}">
                <span class="rosa-role-cov-role">${escapeHtml(r.role)}</span>
                <span class="rosa-role-cov-nums num">${r.owned}/${r.target}</span>
              </div>
            `).join('')}
          </div>
        </div>`
      : '';

    el.innerHTML = `
      <div class="rosa-pitch-header">
        <h2 class="rosa-pitch-title">Formazione tipo</h2>
        <label class="rosa-pitch-module-label" for="rosa-module-select">
          <span class="visually-hidden">Modulo</span>
          <select id="rosa-module-select" class="rosa-pitch-module-select">
            ${MANTRA_MODULE_IDS.map((id) => `<option value="${id}"${id === moduleId ? ' selected' : ''}>${id}</option>`).join('')}
          </select>
        </label>
      </div>
      <div class="rosa-pitch-field" aria-label="Campo con modulo Mantra ${moduleId}">
        <svg class="rosa-pitch-markings" viewBox="0 0 100 100" aria-hidden="true" preserveAspectRatio="none">
          <path d="M0 50H100M0 0H100V100H0ZM50 0V100M50 42A8 8 0 1 0 50 58A8 8 0 1 0 50 42" />
        </svg>
        <div class="rosa-pitch-lines">
          ${groupedLines.map((slots, index) => `
            <div class="rosa-pitch-line rosa-pitch-line--${lineRoles[index]}">
              ${slots.map((slot) => renderSlot(slot, lineRoles[index])).join('')}
            </div>
          `).join('')}
        </div>
      </div>
      ${coverageHtml}
    `;

    el.querySelector('#rosa-module-select')?.addEventListener('change', (event) => {
      const nextModule = event.target.value;
      this.state.rosaSelectedModule = nextModule;
      localStorage.setItem('fantapp:v2:rosaModule', nextModule);
      this._renderRosaPitch();
    });
  },

  _renderRosaModules() {
    const el = document.getElementById('rosa-modules');
    if (!el) return;

    const maxPlayers = Number(this.state.rosaMaxPlayers) || 0;
    const rosterPlayers = this._resolveRosterPlayers();
    const owned = countOwnedByRole(rosterPlayers);
    const modules = computeModuleCoverage(rosterPlayers);

    const cards = modules.map(({ id, filled, total, missing, status }) => {
      const missingText =
        status !== 'complete' && missing.length > 0
          ? `<span class="rosa-module-missing">Manca: ${escapeHtml(missing.join(', '))}</span>`
          : '';
      const roleRows = buildRoleCoverageRows(owned, buildModuleRoleTargets(id, maxPlayers));
      const roleCov = roleRows.length
        ? `<div class="rosa-module-role-cov-mini">${roleRows.map((r) =>
            `<span class="rosa-role-cov rosa-role-cov--${r.status}">${escapeHtml(r.role)} ${r.owned}/${r.target}</span>`
          ).join('')}</div>`
        : '';
      return `
        <div class="rosa-module rosa-module--${escapeHtml(status)}" data-testid="rosa-module-${escapeHtml(id)}">
          <span class="rosa-module-id">${escapeHtml(id)}</span>
          <span class="rosa-module-fill">${filled}/${total}</span>
          ${missingText}
          ${roleCov}
        </div>
      `;
    });

    el.innerHTML = `<div class="rosa-modules-grid">${cards.join('')}</div>`;
  },

  _renderRosaListByRole() {
    const el = document.getElementById('rosa-list-by-role');
    if (!el) return;
    const roster = this.state.roster;
    if (!roster || roster.size === 0) {
      el.innerHTML = `
        <div class="rosa-empty-state" data-testid="rosa-empty-state">
          <p class="rosa-empty-title">Nessun giocatore in Rosa</p>
          <p class="rosa-empty-hint">
            Usa <strong>Preso da me</strong> su un giocatore (con prezzo) per aggiungerlo alla Rosa.
          </p>
        </div>
      `;
      return;
    }

    const allPlayers = this.state.allPlayers || [];
    const grouped = new Map(MANTRA_ROLES.map((role) => [role, []]));
    const others = [];

    for (const [key, entry] of roster.entries()) {
      const player = allPlayers.find((p) => playerToKey(p) === key);
      const name = player ? escapeHtml(player.name) : escapeHtml(key.split('|')[0] || key);
      const team = player ? escapeHtml(player.team) : escapeHtml(key.split('|')[1] || '');
      const rawRoles = player
        ? (Array.isArray(player.roles) && player.roles.length > 0
            ? player.roles
            : String(player.role || '').split(';').map((r) => r.trim()).filter(Boolean))
        : [];
      const roleChipsHtml =
        rawRoles.length > 0
          ? `<div class="roles-container">${rawRoles
              .map((r) => `<span class="${getRoleChipClass(r)}">${escapeHtml(r)}</span>`)
              .join('')}</div>`
          : '';
      const price = entry.price || 0;
      const item = { key, name, team, rawRoles, roleChipsHtml, price };
      const role = primaryMantraRole(rawRoles);
      if (grouped.has(role)) grouped.get(role).push(item);
      else others.push(item);
    }

    const playerChip = ({ key, name, team, roleChipsHtml, price }) => `
      <div class="rosa-player-chip" data-player-key="${escapeHtml(key)}">
        <div class="rosa-player-chip-info">
          ${roleChipsHtml}
          <span class="rosa-player-chip-identity">
            <span class="rosa-player-chip-name" title="${name}">${name}</span>
            ${team ? `<span class="rosa-player-chip-team" title="${team}">${team}</span>` : ''}
          </span>
        </div>
        <span class="rosa-player-chip-price num">${price}M</span>
        <div class="rosa-player-chip-actions">
          <button type="button" class="studio-card-btn rosa-edit-price-btn"
                  data-player-key="${escapeHtml(key)}" data-player-name="${name}" data-player-team="${team}"
                  aria-label="Modifica prezzo di ${name}">Modifica</button>
          <button type="button" class="studio-card-btn studio-card-btn--icon rosa-remove-btn"
                  data-player-key="${escapeHtml(key)}" aria-label="Rimuovi ${name} dalla Rosa">×</button>
        </div>
      </div>
    `;
    const roleSection = (role, items) => {
      const playersHtml = items.length
        ? `<div class="rosa-role-players">${items.map(playerChip).join('')}</div>`
        : '';

      return `
      <details class="rosa-role-section rosa-role-section--${escapeHtml(role.toLowerCase())}${items.length ? '' : ' rosa-role-section--empty'}">
        <summary class="rosa-role-header">
          <span class="rosa-role-dot ${getRoleChipClass(role)}" aria-hidden="true"></span>
          <span class="rosa-role-title">${escapeHtml(role)}</span>
          <span class="rosa-role-target num">${items.length}</span>
          <span class="rosa-role-chevron" aria-hidden="true"></span>
        </summary>
        <div class="rosa-role-body">${playersHtml}</div>
      </details>
    `;
    };

    // Solo ruoli con almeno un preso (chiuse di default).
    const sections = MANTRA_ROLES
      .filter((role) => grouped.get(role).length > 0)
      .map((role) => roleSection(role, grouped.get(role)));
    if (others.length) {
      sections.push(roleSection('Altri', others));
    }
    const ownedTotal = roster.size;
    el.innerHTML = `
  <div class="rosa-list-heading">
    <h2 class="rosa-list-title">Rosa completa</h2>
    <span class="rosa-list-meta">${ownedTotal} GIOCATORI · RUOLI MANTRA</span>
  </div>
  <div class="rosa-role-grid">${sections.join('')}</div>
`;

    el.querySelectorAll('.rosa-remove-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const key = btn.dataset.playerKey;
        if (key && confirm('Rimuovere questo giocatore dalla Rosa?')) {
          this.removeFromRoster(key);
        }
      });
    });

    el.querySelectorAll('.rosa-edit-price-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const key = btn.dataset.playerKey;
        const playerInfo = { name: btn.dataset.playerName, team: btn.dataset.playerTeam };
        if (key) this.showPresoForm(key, playerInfo, btn);
      });
    });
  },
};
